import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import {
	Briefcase,
	Building2,
	Users,
	Star,
	MessageSquare,
	Send,
	Loader2,
	AlertCircle,
	ChevronRight,
	ArrowLeft,
	Mail,
	ShieldCheck,
	Download,
	FileText
} from 'lucide-react';
import {
	Card, CardContent, CardHeader, CardTitle, CardDescription,
	Button, Input, Badge,
	Tabs, TabsContent, TabsList, TabsTrigger,
	useToast
} from '@hiring-intelligence/ui';
import { Logo03Png, Logo01Png } from '@hiring-intelligence/ui';
import { hmPortalApi, accessLinkApi } from '@/services/api';
import { YEARS_OF_EXPERIENCE_LABELS } from '@hiring-intelligence/shared';

/** Format a recommendation timestamp as DD-MM-YYYY HH:MM:SS. Returns null for empty/invalid values. */
function formatRecommendedAt(value: string | null | undefined): string | null {
	if (!value) return null;
	const dt = new Date(value);
	if (isNaN(dt.getTime())) return null;
	const pad = (n: number) => String(n).padStart(2, '0');
	return `${pad(dt.getDate())}-${pad(dt.getMonth() + 1)}-${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(dt.getSeconds())}`;
}

/** Strip wrapping code fences (```markdown … ``` or ``` … ```) so the markdown renders properly */
function stripCodeFences(raw: string): string {
	const trimmed = raw.trim();
	const match = trimmed.match(/^```(?:\w+)?\s*\n([\s\S]*?)\n\s*```$/);
	return match ? match[1] : trimmed;
}

/** Insert GFM header-separator rows into pipe tables that are missing them */
function fixMarkdownTables(md: string): string {
	return md.replace(
		/((?:^|\n)\|[^\n]+\|(?:\n\|[^\n]+\|)+)/g,
		(tableBlock) => {
			const lines = tableBlock.split('\n').filter(l => l.trim());
			if (lines.length >= 2 && /^\|[\s-:|]+\|$/.test(lines[1].trim())) return tableBlock;
			const cols = lines[0].split('|').length - 2;
			const sep = '|' + ' --- |'.repeat(cols);
			return [lines[0], sep, ...lines.slice(1)].join('\n');
		}
	);
}

/** Per-candidate consultant notes — read-only view for the HM */
function ConsultantNotesPanel({ note }: { note: { content: string } | null | undefined }) {
	const [open, setOpen] = useState(false);

	return (
		<div className="mt-3 pt-3 border-t space-y-2">
			<button
				type="button"
				className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
				onClick={() => setOpen(o => !o)}
				disabled={!note?.content}
			>
				<FileText className="h-3.5 w-3.5" />
				{open ? '▾' : '▸'} Consultant notes{!note?.content ? ' (none)' : ''}
			</button>

			{open && note?.content && <CVPreviewBlock content={note.content} />}
			{open && !note?.content && (
				<p className="text-xs text-muted-foreground italic">No consultant notes have been recorded for this candidate yet.</p>
			)}
		</div>
	);
}

/** Per-candidate generated CV — collapsible read-only view for the HM */
function CVPanel({ cv }: { cv: { content: string } | null | undefined }) {
	const [open, setOpen] = useState(false);

	return (
		<div className="mt-3 pt-3 border-t space-y-2">
			<button
				type="button"
				className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
				onClick={() => setOpen(o => !o)}
				disabled={!cv?.content}
			>
				<FileText className="h-3.5 w-3.5" />
				{open ? '▾' : '▸'} CV{!cv?.content ? ' (none)' : ''}
			</button>

			{open && cv?.content && <CVPreviewBlock content={cv.content} />}
			{open && !cv?.content && (
				<p className="text-xs text-muted-foreground italic">No CV has been generated for this candidate yet.</p>
			)}
		</div>
	);
}

/** CV preview block — mirrors apps/web CVPreviewModal rendering rules */
function CVPreviewBlock({ content }: { content: string }) {
	const cleanContent = useMemo(() => fixMarkdownTables(stripCodeFences(content)), [content]);
	return (
		<div className="max-h-96 overflow-y-auto rounded-md bg-white p-4 sm:p-6 text-black border">
			<div className="prose prose-sm max-w-none prose-table:border-collapse prose-th:border-none prose-td:border-none prose-th:py-0.5 prose-td:py-0.5 prose-th:pr-2 prose-th:font-semibold prose-h2:text-base prose-h2:font-bold prose-h2:mt-4 prose-h2:mb-2 prose-h2:border-0 prose-h3:text-sm prose-h3:font-semibold prose-h3:mt-3 prose-h3:mb-1 prose-p:text-sm prose-li:text-sm prose-li:my-0.5">
				<ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{cleanContent}</ReactMarkdown>
			</div>
		</div>
	);
}

// Email verification component
function EmailVerification({ token }: { token: string }) {
	const [email, setEmail] = useState('');
	const { toast } = useToast();

	const verifyMutation = useMutation({
		mutationFn: () => accessLinkApi.verifyEmail(token, email),
		onSuccess: () => {
			(globalThis as any).window.location.reload();
		},
		onError: (error: Error) => {
			toast({ title: 'Verification Failed', description: error.message, variant: 'destructive' });
		}
	});

	return (
		<div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50 flex items-center justify-center">
			<Card className="max-w-md w-full mx-4">
				<CardHeader className="text-center">
					<div className="mx-auto h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
						<Mail className="h-7 w-7 text-primary" />
					</div>
					<CardTitle>Verify Your Email</CardTitle>
					<CardDescription>
						Enter your email address to access the hiring manager portal.
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="space-y-4">
						<Input
							type="email"
							placeholder="your@email.com"
							value={email}
							onChange={(e) => setEmail((e.target as any).value)}
						/>
						<Button
							className="w-full"
							onClick={() => verifyMutation.mutate()}
							disabled={!email || verifyMutation.isPending}
						>
							{verifyMutation.isPending ? (
								<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verifying...</>
							) : (
								<><ShieldCheck className="mr-2 h-4 w-4" /> Verify Email</>
							)}
						</Button>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

// Star rating component
function StarRating({ value, onChange }: { value: number; onChange: (v: number) => void }) {
	const [hover, setHover] = useState(0);

	return (
		<div className="flex gap-1">
			{[1, 2, 3, 4, 5].map((star) => (
				<button
					key={star}
					className="p-1 sm:p-0.5 transition-transform hover:scale-110"
					onMouseEnter={() => setHover(star)}
					onMouseLeave={() => setHover(0)}
					onClick={() => onChange(star)}
				>
					<Star
						className={`h-7 w-7 sm:h-6 sm:w-6 transition-colors ${(hover || value) >= star
							? 'fill-yellow-400 text-yellow-400'
							: 'text-gray-200'
							}`}
					/>
				</button>
			))}
		</div>
	);
}

// Job detail view inside the portal
function JobDetail({ token, job, onBack }: { token: string; job: any; onBack: () => void }) {
	const { toast } = useToast();
	const queryClient = useQueryClient();
	const [newComment, setNewComment] = useState('');
	const [ratingState, setRatingState] = useState<Record<string, number>>({});
	const [feedbackState, setFeedbackState] = useState<Record<string, string>>({});
	const [expandedFeedback, setExpandedFeedback] = useState<Record<string, boolean>>({});
	const [downloadingPdf, setDownloadingPdf] = useState<string | null>(null);

	const handleDownloadPdf = async (cvId: string) => {
		setDownloadingPdf(cvId);
		try {
			const { blob, filename } = await hmPortalApi.downloadCVPDF(token, cvId);
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = filename || 'document.pdf';
			a.click();
			URL.revokeObjectURL(url);
		} catch (e: any) {
			toast({ title: 'Download failed', description: e?.message || 'Could not download the PDF. Please try again.', variant: 'destructive' });
		} finally {
			setDownloadingPdf(null);
		}
	};

	const { data: candidatesData, isLoading: candidatesLoading } = useQuery({
		queryKey: ['hm-candidates', job.id, token],
		queryFn: () => hmPortalApi.getCandidates(token, job.id)
	});

	const { data: commentsData, isLoading: commentsLoading } = useQuery({
		queryKey: ['hm-comments', job.id, token],
		queryFn: () => hmPortalApi.getComments(token, job.id)
	});

	const commentMutation = useMutation({
		mutationFn: () => hmPortalApi.addComment(token, job.id, newComment),
		onSuccess: () => {
			setNewComment('');
			queryClient.invalidateQueries({ queryKey: ['hm-comments', job.id, token] });
			toast({ title: 'Comment Added', description: 'Your comment has been posted.' });
		}
	});

	const rateMutation = useMutation({
		mutationFn: ({ candidateId, rating }: { candidateId: string; rating: number }) =>
			hmPortalApi.rateCandidate(token, job.id, candidateId, rating),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['hm-candidates', job.id, token] });
			toast({ title: 'Rating Saved' });
		}
	});

	const feedbackMutation = useMutation({
		mutationFn: ({ candidateId, feedback }: { candidateId: string; feedback: string }) =>
			hmPortalApi.addFeedback(token, job.id, candidateId, feedback),
		onSuccess: (_data, variables) => {
			setFeedbackState(prev => ({ ...prev, [variables.candidateId]: '' }));
			queryClient.invalidateQueries({ queryKey: ['hm-candidates', job.id, token] });
			toast({ title: 'Feedback Submitted' });
		}
	});

	const candidates = candidatesData?.data || [];
	const comments = commentsData?.data || [];

	return (
		<div>
			<Button variant="ghost" onClick={onBack} className="mb-4">
				<ArrowLeft className="mr-2 h-4 w-4" /> Back to Jobs
			</Button>

			{/* Job Header */}
			<Card className="mb-6">
				<CardHeader>
					<CardTitle className="text-xl">{job.roleTitle}</CardTitle>
					<CardDescription>
						{YEARS_OF_EXPERIENCE_LABELS[job.yearsOfExperience] || job.yearsOfExperience} • {job.location || 'No location'} • {job.company?.name}
					</CardDescription>
				</CardHeader>
			</Card>

			<Tabs defaultValue="candidates" className="w-full">
				<TabsList className="grid w-full grid-cols-2">
					<TabsTrigger value="candidates" className="text-xs sm:text-sm truncate">Candidates ({candidates.length})</TabsTrigger>
					<TabsTrigger value="comments" className="text-xs sm:text-sm truncate">Comments ({comments.length})</TabsTrigger>
				</TabsList>

				{/* Candidates Tab */}
				<TabsContent value="candidates" className="mt-4 space-y-3">
					{candidatesLoading ? (
						<div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
					) : candidates.length === 0 ? (
						<Card>
							<CardContent className="py-12 text-center">
								<Users className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
								<p className="text-muted-foreground">No candidates have been recommended yet.</p>
							</CardContent>
						</Card>
					) : candidates.map((candidate: any) => {
						const existingRating = candidate.ratings?.[0]?.rating || ratingState[candidate.id] || 0;
						const candidateFeedback = candidate.feedback || [];
						const isExpanded = expandedFeedback[candidate.id] || false;
						return (
							<Card key={candidate.id}>
								<CardContent className="p-4">
								<div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
									<div className="flex-1 min-w-0">
										<div className="flex items-center gap-3">
											<div className="h-10 w-10 rounded-full bg-indigo-50 flex items-center justify-center shrink-0">
													<span className="text-sm font-semibold text-indigo-600">
														{candidate.name?.charAt(0).toUpperCase()}
													</span>
												</div>
												<div>
													<div className="font-medium">
														{candidate.name}
													</div>
													<div className="text-sm text-muted-foreground">
														{candidate.currentTitle} {candidate.currentCompany ? `at ${candidate.currentCompany}` : ''}
													</div>
													{candidate.location && (
														<div className="text-sm text-muted-foreground">📍 {candidate.location}</div>
													)}
													{formatRecommendedAt(candidate.recommendedAt) && (
														<div className="text-xs text-muted-foreground mt-0.5">
															🕒 Recommended {formatRecommendedAt(candidate.recommendedAt)}
														</div>
													)}
												</div>
											</div>
										</div>
										<div className="flex flex-wrap items-center gap-2 sm:flex-col sm:items-end">
											<StarRating
												value={existingRating}
												onChange={(rating) => {
													setRatingState(prev => ({ ...prev, [candidate.id]: rating }));
													rateMutation.mutate({ candidateId: candidate.id, rating });
												}}
											/>
											{candidate.generatedCV && (
												<Button
													variant="outline"
													size="sm"
													className="text-xs"
													onClick={() => handleDownloadPdf(candidate.generatedCV.id)}
													disabled={downloadingPdf === candidate.generatedCV.id}
												>
													{downloadingPdf === candidate.generatedCV.id
														? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
														: <Download className="h-3 w-3 mr-1" />}
													CV PDF
												</Button>
											)}
											{candidate.consultantNotes && candidate.cvType !== 'blind' && (
												<Button
													variant="outline"
													size="sm"
													className="text-xs"
													onClick={() => handleDownloadPdf(candidate.consultantNotes.id)}
													disabled={downloadingPdf === candidate.consultantNotes.id}
												>
													{downloadingPdf === candidate.consultantNotes.id
														? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
														: <Download className="h-3 w-3 mr-1" />}
													Notes PDF
												</Button>
											)}
										</div>
									</div>

									<CVPanel cv={candidate.generatedCV} />

									<ConsultantNotesPanel note={candidate.consultantNotes} />

								<div className="mt-3 pt-3 border-t space-y-2">
									{/* Existing feedback */}
									{candidateFeedback.length > 0 && (
											<div className="space-y-1.5">
												<button
													className="text-xs font-medium text-muted-foreground hover:text-foreground"
													onClick={() => setExpandedFeedback(prev => ({ ...prev, [candidate.id]: !isExpanded }))}
												>
													{isExpanded ? '▾' : '▸'} {candidateFeedback.length} feedback(s)
												</button>
												{isExpanded && candidateFeedback.map((fb: any) => (
													<div key={fb.id} className="text-sm bg-muted/50 rounded p-2">
														<p>{fb.feedback}</p>
														<p className="text-xs text-muted-foreground mt-1">
															— {fb.contact?.name || fb.contact?.email || 'HM'} • {new Date(fb.createdAt).toLocaleString()}
														</p>
													</div>
												))}
											</div>
										)}

									{/* Add feedback */}
									<div className="flex flex-col sm:flex-row gap-2">
											<Input
												placeholder="Write feedback for this candidate..."
												value={feedbackState[candidate.id] || ''}
												onChange={(e) => setFeedbackState(prev => ({ ...prev, [candidate.id]: (e.target as any).value }))}
												onKeyDown={(e) => {
													if (e.key === 'Enter' && (feedbackState[candidate.id] || '').trim()) {
														feedbackMutation.mutate({ candidateId: candidate.id, feedback: feedbackState[candidate.id] });
													}
												}}
											/>
											<Button
												size="sm"
												onClick={() => feedbackMutation.mutate({ candidateId: candidate.id, feedback: feedbackState[candidate.id] || '' })}
												disabled={!(feedbackState[candidate.id] || '').trim() || feedbackMutation.isPending}
											>
												{feedbackMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
											</Button>
										</div>
									</div>
								</CardContent>
							</Card>
						);
					})}
				</TabsContent>

				{/* Comments Tab */}
				<TabsContent value="comments" className="mt-4 space-y-4">
					{/* Add Comment */}
					<Card>
						<CardContent className="p-4">
								<div className="flex flex-col sm:flex-row gap-2">
								<Input
									placeholder="Write a comment..."
									value={newComment}
									onChange={(e) => setNewComment((e.target as any).value)}
									onKeyDown={(e) => e.key === 'Enter' && newComment.trim() && commentMutation.mutate()}
								/>
								<Button
									onClick={() => commentMutation.mutate()}
									disabled={!newComment.trim() || commentMutation.isPending}
								>
									{commentMutation.isPending ? (
										<Loader2 className="h-4 w-4 animate-spin" />
									) : (
										<Send className="h-4 w-4" />
									)}
								</Button>
							</div>
						</CardContent>
					</Card>

					{commentsLoading ? (
						<div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
					) : comments.length === 0 ? (
						<Card>
							<CardContent className="py-12 text-center">
								<MessageSquare className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
								<p className="text-muted-foreground">No comments yet. Be the first to share your thoughts!</p>
							</CardContent>
						</Card>
					) : comments.map((comment: any) => (
						<Card key={comment.id}>
							<CardContent className="p-4">
								<p className="text-sm">{comment.comment}</p>
								<p className="text-xs text-muted-foreground mt-2">
									{new Date(comment.createdAt).toLocaleString()}
								</p>
							</CardContent>
						</Card>
					))}
				</TabsContent>
			</Tabs>
		</div>
	);
}

// Main HM Portal page
export default function HiringManagerPortalPage() {
	const { token } = useParams<{ token: string }>();
	const [selectedJob, setSelectedJob] = useState<any>(null);

	// Validate token first
	const { data: validationData, isLoading: validating, error: validationError } = useQuery({
		queryKey: ['hm-validate', token],
		queryFn: () => accessLinkApi.validateToken(token!),
		enabled: !!token
	});

	const { data: jobsData, isLoading: jobsLoading } = useQuery({
		queryKey: ['hm-jobs', token],
		queryFn: () => hmPortalApi.getJobs(token!),
		enabled: !!token && (validationData?.data?.emailVerified === true)
	});

	if (validating) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<Loader2 className="h-8 w-8 animate-spin text-gray-400" />
			</div>
		);
	}

	if (validationError) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<Card className="max-w-md w-full mx-4">
					<CardContent className="flex flex-col items-center py-12">
						<AlertCircle className="h-12 w-12 text-red-500 mb-4" />
						<h2 className="text-xl font-semibold mb-2">Link Invalid or Expired</h2>
						<p className="text-muted-foreground text-center">
							Please contact your recruiter for a new access link.
						</p>
					</CardContent>
				</Card>
			</div>
		);
	}

	// Email not verified → show verification form
	if (validationData?.data && !validationData.data.emailVerified) {
		return <EmailVerification token={token!} />;
	}

	const jobs = jobsData?.data || [];
	const linkInfo = validationData?.data || {};
	// Company name can come from a company-level link, or from the first job (legacy per-HR link)
	const portalCompanyName: string | null =
		linkInfo.company?.name ||
		jobs[0]?.company?.name ||
		null;
	const signedInEmail: string | null = linkInfo.email || null;

	return (
		<div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50">
			<div className="max-w-4xl mx-auto px-4 py-8">
				{/* Header */}
				{!selectedJob && (
					<div className="text-center mb-8">
						<div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-primary/10 mb-4">
							<img src={Logo03Png} alt="Canis Major" className="h-7 w-7" />
						</div>
						<div className="flex justify-center mb-2">
							<img src={Logo01Png} alt="Canis Major" className="h-8" />
						</div>
						<h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Hiring Manager Portal</h1>
						<p className="text-gray-500 mt-2">Review candidates and provide feedback</p>
						{(portalCompanyName || signedInEmail) && (
							<div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-sm">
								{portalCompanyName && (
									<Badge variant="secondary" className="gap-1.5">
										<Building2 className="h-3.5 w-3.5" />
										{portalCompanyName}
									</Badge>
								)}
								{signedInEmail && (
									<Badge variant="outline" className="gap-1.5">
										<Mail className="h-3.5 w-3.5" />
										Signed in as {signedInEmail}
									</Badge>
								)}
							</div>
						)}
					</div>
				)}

				{/* Show either job list or job detail */}
				{selectedJob ? (
					<JobDetail token={token!} job={selectedJob} onBack={() => setSelectedJob(null)} />
				) : (
					<>
						{jobsLoading ? (
							<div className="text-center py-16"><Loader2 className="h-8 w-8 animate-spin mx-auto" /></div>
						) : jobs.length === 0 ? (
							<Card>
								<CardContent className="py-16 text-center">
									<Briefcase className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
									<h3 className="text-lg font-medium mb-2">No jobs available</h3>
									<p className="text-muted-foreground">There are no open positions to review at this time.</p>
								</CardContent>
							</Card>
						) : (
							<div className="space-y-4">
								{jobs.map((job: any) => (
									<Card
										key={job.id}
										className="hover:shadow-md transition-all cursor-pointer group"
										onClick={() => setSelectedJob(job)}
									>
									<CardContent className="p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
										<div className="min-w-0">
											<h3 className="text-base sm:text-lg font-semibold group-hover:text-primary transition-colors truncate">
													{job.roleTitle}
												</h3>
											{job.company?.name && (
												<div className="flex items-center gap-1.5 mt-1 text-sm text-muted-foreground">
													<Building2 className="h-3.5 w-3.5" />
													<span className="truncate">{job.company.name}</span>
												</div>
											)}
											<div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-muted-foreground">
													<Badge variant="secondary">{YEARS_OF_EXPERIENCE_LABELS[job.yearsOfExperience] || job.yearsOfExperience}</Badge>
													{job.location && <span>📍 {job.location}</span>}
												</div>
												<div className="flex flex-wrap items-center gap-3 sm:gap-4 mt-3 text-sm">
													<span className="flex items-center gap-1 text-muted-foreground">
														<Users className="h-4 w-4" />
														{job.recommendedCandidates} candidate(s)
													</span>
													<span className="flex items-center gap-1 text-muted-foreground">
														<MessageSquare className="h-4 w-4" />
														{job.myComments} comment(s)
													</span>
												</div>
											</div>
										<ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors hidden sm:block" />
										</CardContent>
									</Card>
								))}
							</div>
						)}
					</>
				)}

				<p className="text-center text-xs text-gray-400 mt-8">
					Powered by Canis Major
				</p>
			</div>
		</div>
	);
}
